import { Json } from '@/integrations/supabase/types';

/**
 * Type-safe utility to parse Json fields from Supabase into expected types
 */

export interface PerplexityInsight {
  fact: string;
  context: string;
  source?: string;
  verdict?: string;
  verdict_note?: string;
  final_source_url?: string;
  final_source_name?: string;
  checker_source_url?: string;
  checker_source_name?: string;
  original_source_url?: string;
  original_source_name?: string;
  final_source_priority?: string;
  checker_source_priority?: string;
  original_source_priority?: string;
}

export interface PerplexityInsightsData {
  insights: PerplexityInsight[];
  citations?: string[];
  search_results?: Array<{
    title: string;
    url: string;
    snippet?: string;
    date?: string;
  }>;
  metadata?: {
    model: string;
    created: number;
  };
}

/**
 * Safely parse a Json field as a string array
 */
export const parseJsonAsStringArray = (json: Json | null | undefined): string[] => {
  if (!json) return [];
  if (Array.isArray(json)) {
    return json.filter((item): item is string => typeof item === 'string');
  }
  if (typeof json === 'string') {
    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed)) {
        return parsed.filter((item): item is string => typeof item === 'string');
      }
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Safely parse perplexity insights from Json
 */
export const parsePerplexityInsights = (json: Json | null | undefined): PerplexityInsightsData | null => {
  if (!json) return null;
  
  try {
    let data: unknown = json;
    
    // If it's a string, try to parse it
    if (typeof json === 'string') {
      data = JSON.parse(json);
    }
    
    // Check if it has the expected structure
    if (data && typeof data === 'object' && 'insights' in data) {
      const insights = (data as { insights: unknown }).insights;
      if (Array.isArray(insights)) {
        return data as PerplexityInsightsData;
      }
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * Check if Json is a valid array with length
 */
export const getJsonArrayLength = (json: Json | null | undefined): number => {
  if (!json) return 0;
  if (Array.isArray(json)) return json.length;
  if (typeof json === 'string') {
    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed)) return parsed.length;
    } catch {
      return 0;
    }
  }
  return 0;
};

/**
 * Safely get insights array from perplexity data
 */
export const getInsightsArray = (json: Json | null | undefined): PerplexityInsight[] => {
  const parsed = parsePerplexityInsights(json);
  return parsed?.insights || [];
};

/**
 * Get citations from perplexity data
 */
export const getCitations = (json: Json | null | undefined): string[] => {
  const parsed = parsePerplexityInsights(json);
  return parsed?.citations || [];
};

/**
 * Extract the domain from a parenthesized hint in source text, e.g.
 * "Badanie na pływakach (potreningu.pl)" → "potreningu.pl"
 */
const extractDomainHint = (sourceText: string): string | null => {
  const match = sourceText.match(/\(([a-z0-9.-]+\.[a-z]{2,})\)/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * Extract any parenthesized keyword from source text for fuzzy URL matching, e.g.
 * "Systematic Review (PMC7739364)" → "pmc7739364"
 * "Journal Study (Corporate Yoga Source)" → "corporate yoga source"
 */
const extractParenKeyword = (sourceText: string): string | null => {
  const match = sourceText.match(/\(([^)]+)\)\s*$/);
  return match ? match[1].toLowerCase().trim() : null;
};

/**
 * Resolve citation URL from source text and available citation data.
 *
 * Resolution order:
 * 1. Source text is itself a URL → return it
 * 2. Bracketed reference [n] → citations[n-1] (Perplexity indexes into citations)
 * 3. Domain hint in parentheses (e.g. "potreningu.pl") → URL containing that domain
 * 4. Keyword hint in parentheses (e.g. "PMC7739364") → URL containing that keyword
 * 5. Significant words from source text → URL containing those words
 * 6. Fallback → first available citation
 */
export const getCitationUrl = (
  sourceText: string,
  perplexityData?: { citations?: string[]; search_results?: Array<{ url: string; title?: string }> } | string[] | null
): string | null => {
  if (!sourceText || !perplexityData) return null;

  // Source text is a direct URL
  if (sourceText.startsWith('http')) return sourceText;

  // Normalize: plain string[] (citations) → object form
  let citations: string[] = [];
  let searchResults: Array<{ url: string; title?: string }> = [];

  if (Array.isArray(perplexityData)) {
    citations = perplexityData;
  } else {
    let parsed = perplexityData;
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { return null; }
    }
    citations = (parsed as { citations?: string[] }).citations || [];
    searchResults = (parsed as { search_results?: Array<{ url: string; title?: string }> }).search_results || [];
  }

  // search_results URLs are supplementary; use them only when citations are empty
  const searchResultUrls = searchResults.map(r => r.url);
  const allUrls = citations.length > 0 ? citations : searchResultUrls;

  if (allUrls.length === 0) return null;

  // 1. Bracketed reference [n] — always resolves against citations
  const bracketMatch = sourceText.match(/\[(\d+)\]/);
  if (bracketMatch) {
    const index = parseInt(bracketMatch[1]) - 1;
    if (citations.length > 0) {
      return citations[index] || citations[0];
    }
    return searchResultUrls[index] || searchResultUrls[0] || null;
  }

  // Combine all URLs for matching
  const combinedUrls = Array.from(new Set([...allUrls, ...searchResultUrls]));

  // 2. Domain hint in parentheses — match against all available URLs
  const domainHint = extractDomainHint(sourceText);
  if (domainHint) {
    const matched = combinedUrls.find(url => url.toLowerCase().includes(domainHint));
    if (matched) return matched;
  }

  // 3. Parenthesized keyword (e.g. "PMC7739364", "Corporate Yoga Source") — match against URLs and search result titles
  const keyword = extractParenKeyword(sourceText);
  if (keyword) {
    // Try matching keyword against URLs (handles PMC IDs like PMC7739364)
    const urlMatch = combinedUrls.find(url => url.toLowerCase().includes(keyword));
    if (urlMatch) return urlMatch;

    // Try matching keyword fragments against URLs (e.g. "PMC7739364" in pmc.ncbi.nlm.nih.gov/articles/PMC7739364)
    const keywordParts = keyword.split(/[\s,/-]+/).filter(p => p.length > 3);
    for (const part of keywordParts) {
      const partMatch = combinedUrls.find(url => url.toLowerCase().includes(part));
      if (partMatch) return partMatch;
    }

    // Try matching against search_results titles
    if (searchResults.length > 0) {
      const titleMatch = searchResults.find(r =>
        r.title && keyword.split(/\s+/).some(word =>
          word.length > 3 && r.title!.toLowerCase().includes(word)
        )
      );
      if (titleMatch) return titleMatch.url;
    }
  }

  // 4. Last resort: match significant words from the full source text against URLs
  const sourceWords = sourceText.toLowerCase().split(/[\s,\-–()[\]]+/).filter(w => w.length > 5);
  for (const word of sourceWords) {
    const wordMatch = combinedUrls.find(url => url.toLowerCase().includes(word));
    if (wordMatch) return wordMatch;
  }

  // 5. Fallback
  return allUrls[0];
};
